import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { CheckCircle2, Mail, Phone, MapPin, MessageCircle, Send } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useSections, isSectionOn } from "@/hooks/useSections";

export default function Contact() {
  const { t, lang } = useI18n();
  const settings = useSiteSettings();
  const sections = useSections();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", consent: true });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const pageTitle = (lang === "ar" ? settings.contact_title_ar : settings.contact_title_en)
    || (lang === "ar" ? "تواصل" : "Contact");
  const heroHeading = (lang === "ar" ? settings.contact_intro_ar : settings.contact_intro_en)
    || (lang === "ar" ? "لنبدأ محادثة" : "Let's Start a Conversation");
  const telegramHref = settings.telegram
    ? (/^https?:\/\//i.test(settings.telegram)
        ? settings.telegram
        : `https://t.me/${settings.telegram.replace(/^@/, "")}`)
    : null;

  return (
    <div data-testid="contact-page">
      <section className="bg-navy text-cream py-24">
        <div className="container-x">
          <span className="eyebrow" data-testid="contact-title">{pageTitle}</span>
          <h1 className="mt-4 text-4xl md:text-6xl" data-testid="contact-hero-heading">{heroHeading}</h1>
          <span className="divider-gold" />
        </div>
      </section>

      <section className="container-x py-16 grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2 space-y-6">
          {settings.contact_email && (
            <a href={`mailto:${settings.contact_email}`} className="card-elev p-6 block hover:-translate-y-0.5 transition-transform" data-testid="contact-email">
              <div className="text-gold"><Mail size={20} /></div>
              <div className="mt-3 text-navy font-medium">{settings.contact_email}</div>
              <div className="text-xs uppercase tracking-widest text-navy/60 mt-1">{lang === "ar" ? "بريد إلكتروني" : "Email"}</div>
            </a>
          )}
          {settings.contact_phone && (
            <a href={`tel:${settings.contact_phone.replace(/\s+/g, "")}`} className="card-elev p-6 block hover:-translate-y-0.5 transition-transform" data-testid="contact-phone">
              <div className="text-gold"><Phone size={20} /></div>
              <div className="mt-3 text-navy font-medium">{settings.contact_phone}</div>
              <div className="text-xs uppercase tracking-widest text-navy/60 mt-1">{lang === "ar" ? "هاتف" : "Phone"}</div>
            </a>
          )}
          {settings.whatsapp && (
            <a
              href={`https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="card-elev p-6 block hover:-translate-y-0.5 transition-transform"
              data-testid="contact-whatsapp"
            >
              <div className="text-gold"><MessageCircle size={20} /></div>
              <div className="mt-3 text-navy font-medium">{settings.whatsapp}</div>
              <div className="text-xs uppercase tracking-widest text-navy/60 mt-1">{lang === "ar" ? "واتساب" : "WhatsApp"}</div>
            </a>
          )}
          {telegramHref && (
            <a
              href={telegramHref}
              target="_blank"
              rel="noopener noreferrer"
              className="card-elev p-6 block hover:-translate-y-0.5 transition-transform"
              data-testid="contact-telegram"
            >
              <div className="text-gold"><Send size={20} /></div>
              <div className="mt-3 text-navy font-medium">{settings.telegram}</div>
              <div className="text-xs uppercase tracking-widest text-navy/60 mt-1">{lang === "ar" ? "تيليجرام" : "Telegram"}</div>
            </a>
          )}
          {(settings.address_ar || settings.address_en) && (
            <div className="card-elev p-6" data-testid="contact-address">
              <div className="text-gold"><MapPin size={20} /></div>
              <div className="mt-3 text-navy font-medium">
                {(lang === "ar" ? settings.address_ar : settings.address_en) || settings.address_en || settings.address_ar}
              </div>
              <div className="text-xs uppercase tracking-widest text-navy/60 mt-1">{lang === "ar" ? "المقر الرئيسي" : "Head Office"}</div>
              {(settings.working_hours_ar || settings.working_hours_en) && (
                <div className="text-xs text-navy/60 mt-2">
                  {(lang === "ar" ? settings.working_hours_ar : settings.working_hours_en) || settings.working_hours_en || settings.working_hours_ar}
                </div>
              )}
            </div>
          )}
          {(!settings.contact_email && !settings.contact_phone && !settings.whatsapp && !settings.telegram && !settings.address_ar && !settings.address_en) && (
            <div className="card-elev p-6 text-navy/50 text-sm" data-testid="contact-none">
              {lang === "ar"
                ? "بيانات التواصل لم تُضبط بعد — يمكنك إرسال الرسالة عبر النموذج."
                : "Contact details are not configured yet — please use the form."}
            </div>
          )}
        </div>

        <div className="md:col-span-3">
          {!isSectionOn(sections, "contact") ? (
            <div className="card-elev p-10 text-navy/60" data-testid="contact-form-off">
              {lang === "ar" ? "نموذج التواصل غير متاح حالياً." : "Contact form is currently unavailable."}
            </div>
          ) : done ? (
            <div className="card-elev p-10" data-testid="contact-thank-you">
              <CheckCircle2 size={40} className="text-gold" />
              <h2 className="mt-4 text-3xl text-navy">{t("common.thankYou")}</h2>
            </div>
          ) : (
            <form onSubmit={submit} className="card-elev p-8 md:p-10 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input required data-testid="c-name" placeholder={t("common.name")} className="field-box" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input required type="email" data-testid="c-email" placeholder={t("common.email")} className="field-box" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <input data-testid="c-phone" placeholder={t("common.phone")} className="field-box" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input data-testid="c-subject" placeholder={t("common.subject")} className="field-box" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              <textarea required data-testid="c-message" rows={6} placeholder={t("common.message")} className="field-box" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <button disabled={loading} data-testid="c-submit" type="submit" className="btn-gold w-full justify-center disabled:opacity-40">
                {loading ? "..." : t("common.send")}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
